import { FC, useEffect } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z, ZodType } from 'zod';
import {
  Grid,
  Typography,
  FormControlLabel,
  Checkbox,
  Button,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import StructuredModal from '../../../../../components/structuredModal/structuredModal';
import ControlledSelectField from '../../../../../components/controlledComponents/ControlledSelectField.component';
import ControlledTextField from '../../../../../components/controlledComponents/ControlledTextField.component';
import {
  TRoute,
  TRouteStop,
  TViewType,
} from 'src/apis/administration/administration.interfaces';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useRoutesAndScheduleState,
  useRoutesAndScheduleActions,
} from '../../../contexts/routesAndSchedule.context';
import { zNumberFromInput } from 'src/utils/zodFormHelpers';

type RouteStopFormData = {
  id?: string;
  stopName: string;
  location: string;
  pickupTime: string;
  dropTime: string;
};

type RouteFormData = {
  routeName: string;
  startLocation: string;
  endLocation: string;
  distance: number;
  estimatedDuration: number;
  isActive: boolean;
  assignedVehicleId?: string;
  assignedDriverId?: string;
  morningPickupTime: string;
  afternoonDropTime: string;
  stops: RouteStopFormData[];
};

const getRouteFormSchema = (
  translateText: (key: string) => string
): ZodType<RouteFormData> =>
  z.object({
    routeName: z.string().min(1, translateText('common.fieldRequired')),
    startLocation: z.string().min(1, translateText('common.fieldRequired')),
    endLocation: z.string().min(1, translateText('common.fieldRequired')),
    distance: zNumberFromInput({
      min: 0.1,
      message: translateText('common.fieldRequired'),
    }),
    estimatedDuration: zNumberFromInput({
      min: 1,
      message: translateText('common.fieldRequired'),
    }),
    isActive: z.boolean(),
    assignedVehicleId: z.string().optional(),
    assignedDriverId: z.string().optional(),
    morningPickupTime: z.string().min(1, translateText('common.fieldRequired')),
    afternoonDropTime: z.string().min(1, translateText('common.fieldRequired')),
    stops: z.array(
      z.object({
        id: z.string().optional(),
        stopName: z.string().min(1, translateText('common.fieldRequired')),
        location: z.string().min(1, translateText('common.fieldRequired')),
        pickupTime: z.string().min(1, translateText('common.fieldRequired')),
        dropTime: z.string().min(1, translateText('common.fieldRequired')),
      })
    ),
  });

const AddEditRouteDetailsView: FC = () => {
  const { translateText } = useTranslation();
  const routeFormSchema = getRouteFormSchema(translateText);
  const { selectedRoute, editMode, viewType, vehicles, drivers } =
    useRoutesAndScheduleState();
  const { setEditMode, setViewType, addRoute, updateRoute } =
    useRoutesAndScheduleActions();

  const isEditMode = editMode === 'edit' && viewType === TViewType.ROUTES;
  const isCreateMode = editMode === 'create' && viewType === TViewType.ROUTES;
  const isOpen = (isEditMode || isCreateMode) && viewType === TViewType.ROUTES;

  const defaultValues: RouteFormData = {
    routeName: '',
    startLocation: '',
    endLocation: '',
    distance: 0,
    estimatedDuration: 0,
    isActive: true,
    assignedVehicleId: '',
    assignedDriverId: '',
    morningPickupTime: '',
    afternoonDropTime: '',
    stops: [],
  };

  const methods = useForm<RouteFormData>({
    resolver: zodResolver(routeFormSchema),
    mode: 'onBlur',
    defaultValues,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'stops',
  });

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && selectedRoute) {
        reset({
          routeName: selectedRoute.routeName || '',
          startLocation: selectedRoute.startLocation || '',
          endLocation: selectedRoute.endLocation || '',
          distance: selectedRoute.distance || 0,
          estimatedDuration: selectedRoute.estimatedDuration || 0,
          isActive: selectedRoute.isActive ?? true,
          assignedVehicleId: selectedRoute.assignedVehicleId || '',
          assignedDriverId: selectedRoute.assignedDriverId || '',
          morningPickupTime: selectedRoute.morningPickupTime || '',
          afternoonDropTime: selectedRoute.afternoonDropTime || '',
          stops:
            selectedRoute.stops?.map((stop) => ({
              id: stop.id,
              stopName: stop.stopName || '',
              location: stop.location || '',
              pickupTime: stop.pickupTime || '',
              dropTime: stop.dropTime || '',
            })) || [],
        });
      } else if (isCreateMode) {
        reset(defaultValues);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRoute, isEditMode, isCreateMode, isOpen, reset]);

  const onSubmitForm = (data: RouteFormData) => {
    const stops: TRouteStop[] = (data.stops || []).map((stop) => ({
      id: stop.id || '',
      stopName: stop.stopName,
      location: stop.location,
      pickupTime: stop.pickupTime,
      dropTime: stop.dropTime,
      students: [],
    }));

    const routeData: TRoute = {
      id: selectedRoute?.id || '',
      routeName: data.routeName,
      startLocation: data.startLocation,
      endLocation: data.endLocation,
      distance: data.distance,
      estimatedDuration: data.estimatedDuration,
      isActive: data.isActive,
      assignedVehicleId: data.assignedVehicleId,
      assignedDriverId: data.assignedDriverId,
      morningPickupTime: data.morningPickupTime,
      afternoonDropTime: data.afternoonDropTime,
      stops,
    };

    if (isEditMode) {
      updateRoute(routeData);
    } else {
      addRoute(routeData);
    }
    setEditMode(undefined);
    setViewType(undefined);
  };

  const handleClose = () => {
    setEditMode(undefined);
    setViewType(undefined);
  };

  const addStop = () => {
    append({
      stopName: '',
      location: '',
      pickupTime: '',
      dropTime: '',
    });
  };

  const vehicleOptions = vehicles.map((vehicle) => ({
    key: vehicle.id,
    value: vehicle.vehicleNumber,
  }));

  const driverOptions = drivers.map((driver) => ({
    key: driver.id,
    value: driver.name,
  }));

  if (!isOpen) {
    return null;
  }

  return (
    <div data-testid="add-edit-route-container">
      <StructuredModal
        isOpen={isOpen}
        onClose={handleClose}
        title={
          isEditMode
            ? translateText('administration.routesAndSchedule.editRoute') ||
            'Edit Route'
            : translateText('administration.routesAndSchedule.addRoute') ||
            'Add Route'
        }
        drawerWidth="80vw"
        footerPositiveAction={handleSubmit(onSubmitForm)}
        footerPositiveActionText={
          isEditMode
            ? translateText('common.update')
            : translateText('common.save')
        }
        isLoading={false}
        footerNegativeAction={handleClose}
        footerNegativeActionText={translateText('common.cancel')}
      >
        <FormProvider {...methods}>
          <form
            onSubmit={handleSubmit(onSubmitForm)}
            style={{ width: '100%' }}
            data-testid="route-form"
          >
            <Grid
              container
              spacing={3}
              padding={2}
              data-testid="route-form-container"
            >
              <Grid item xs={12} data-testid="route-basic-section">
                <Typography variant="h6" gutterBottom>
                  {translateText(
                    'administration.routesAndSchedule.details.basicInfo'
                  ) || 'Basic Information'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} data-testid="route-name-field">
                <ControlledTextField
                  control={control}
                  name="routeName"
                  id="route-name"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.routeName'
                    ) || 'Route Name'
                  }
                  errorMessage={errors.routeName?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="route-status-field">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watch('isActive')}
                      onChange={(e) => {
                        methods.setValue('isActive', e.target.checked);
                      }}
                    />
                  }
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.status'
                    ) || 'Active'
                  }
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="route-start-location-field"
              >
                <ControlledTextField
                  control={control}
                  name="startLocation"
                  id="route-start-location"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.startLocation'
                    ) || 'Start Location'
                  }
                  errorMessage={errors.startLocation?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="route-end-location-field">
                <ControlledTextField
                  control={control}
                  name="endLocation"
                  id="route-end-location"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.endLocation'
                    ) || 'End Location'
                  }
                  errorMessage={errors.endLocation?.message}
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="route-distance-field">
                <ControlledTextField
                  control={control}
                  name="distance"
                  id="route-distance"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.distance'
                    ) || 'Distance (km)'
                  }
                  errorMessage={errors.distance?.message}
                  type="number"
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="route-duration-field">
                <ControlledTextField
                  control={control}
                  name="estimatedDuration"
                  id="route-duration"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.duration'
                    ) || 'Duration (minutes)'
                  }
                  errorMessage={errors.estimatedDuration?.message}
                  type="number"
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="route-schedule-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.schedule'
                  ) || 'Schedule'}
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="route-morning-pickup-field"
              >
                <ControlledTextField
                  control={control}
                  name="morningPickupTime"
                  id="route-morning-pickup"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.morningPickup'
                    ) || 'Morning Pickup Time'
                  }
                  errorMessage={errors.morningPickupTime?.message}
                  type="time"
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid
                item
                xs={12}
                md={6}
                data-testid="route-afternoon-drop-field"
              >
                <ControlledTextField
                  control={control}
                  name="afternoonDropTime"
                  id="route-afternoon-drop"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.afternoonDrop'
                    ) || 'Afternoon Drop Time'
                  }
                  errorMessage={errors.afternoonDropTime?.message}
                  type="time"
                  required
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="route-assignments-section">
                <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                  {translateText(
                    'administration.routesAndSchedule.details.assignments'
                  ) || 'Assignments'}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6} data-testid="route-vehicle-field">
                <ControlledSelectField
                  control={control}
                  name="assignedVehicleId"
                  id="route-vehicle"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.vehicle'
                    ) || 'Vehicle'
                  }
                  itemList={vehicleOptions}
                  errorMessage={errors.assignedVehicleId?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} md={6} data-testid="route-driver-field">
                <ControlledSelectField
                  control={control}
                  name="assignedDriverId"
                  id="route-driver"
                  label={
                    translateText(
                      'administration.routesAndSchedule.columns.driver'
                    ) || 'Driver'
                  }
                  itemList={driverOptions}
                  errorMessage={errors.assignedDriverId?.message}
                  isGridItem={false}
                />
              </Grid>

              <Grid item xs={12} data-testid="route-stops-section">
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="h6">
                    {translateText(
                      'administration.routesAndSchedule.details.stops'
                    ) || 'Route Stops'}
                  </Typography>
                  <Button
                    startIcon={<Add />}
                    onClick={addStop}
                    variant="outlined"
                    size="small"
                    data-testid="add-stop-button"
                  >
                    {translateText(
                      'administration.routesAndSchedule.addStop'
                    ) || 'Add Stop'}
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              {fields.map((field, index) => (
                <Grid
                  item
                  xs={12}
                  key={field.id}
                  data-testid={`route-stop-${index}`}
                >
                  <Box
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 2,
                      }}
                    >
                      <Typography variant="subtitle2">
                        {translateText('administration.routesAndSchedule.stop')}{' '}
                        {index + 1}
                      </Typography>
                      <IconButton
                        onClick={() => remove(index)}
                        size="small"
                        color="error"
                        data-testid={`remove-stop-${index}`}
                      >
                        <Delete />
                      </IconButton>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <ControlledTextField
                          control={control}
                          name={`stops.${index}.stopName`}
                          id={`stop-${index}-name`}
                          label={
                            translateText(
                              'administration.routesAndSchedule.stopName'
                            ) || 'Stop Name'
                          }
                          errorMessage={
                            errors.stops?.[index]?.stopName?.message
                          }
                          required
                          isGridItem={false}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ControlledTextField
                          control={control}
                          name={`stops.${index}.location`}
                          id={`stop-${index}-location`}
                          label={
                            translateText(
                              'administration.routesAndSchedule.location'
                            ) || 'Location'
                          }
                          errorMessage={
                            errors.stops?.[index]?.location?.message
                          }
                          required
                          isGridItem={false}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ControlledTextField
                          control={control}
                          name={`stops.${index}.pickupTime`}
                          id={`stop-${index}-pickup-time`}
                          label={
                            translateText(
                              'administration.routesAndSchedule.details.pickupTime'
                            ) || 'Pickup Time'
                          }
                          errorMessage={
                            errors.stops?.[index]?.pickupTime?.message
                          }
                          type="time"
                          required
                          isGridItem={false}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <ControlledTextField
                          control={control}
                          name={`stops.${index}.dropTime`}
                          id={`stop-${index}-drop-time`}
                          label={
                            translateText(
                              'administration.routesAndSchedule.details.dropTime'
                            ) || 'Drop Time'
                          }
                          errorMessage={
                            errors.stops?.[index]?.dropTime?.message
                          }
                          type="time"
                          required
                          isGridItem={false}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </form>
        </FormProvider>
      </StructuredModal>
    </div>
  );
};

export default AddEditRouteDetailsView;
